import { gql, useQuery } from '@apollo/client'

import { KmdEasyEnabledGate } from './__generated__/types'

/**
 * The KMD Easy activation gate — a NARROW document on purpose.
 *
 * The company-settings landing page used to read this one boolean off
 * `GetKmdEasySettings`, the ~30-field FTP/auto-export settings document. That
 * couples an integration-visibility gate to every field in that document: the
 * moment one of them lags a backend deploy, the whole query errors, `data` is
 * undefined, and — because the gate is fail-closed (below) — the integration
 * disappears for tenants that DO have it. Same discipline the feature-config
 * module follows (`src/api/my-feature-config.ts`): a gate reads its own
 * minimal, tolerant document so an out-of-band field deploy elsewhere cannot
 * black out the surface it guards.
 */
export const GET_KMD_EASY_ENABLED_GATE = gql`
  query KmdEasyEnabledGate {
    kmdEasyCompanySettings {
      id
      enabled
    }
  }
`

/**
 * Whether the current user's company has the KMD Easy integration.
 *
 * FAIL-CLOSED, and that is the whole point (PRO-565 rework, QA rejection):
 * the backend resolver serves `kmdEasyCompanySettings` ONLY for a company that
 * has the integration and returns `null` for a disabled or never-configured
 * one — an explicit `enabled: false` never crosses the wire. A gate written as
 * `enabled === false` therefore never fires and leaks the surface to every
 * KMD-less tenant. Only a positive `enabled: true` read counts as enabled;
 * `null`, a query still in flight, and an errored read all read as OFF.
 *
 * `enabled` is not tenant-settable from the app (no WasteHero-facing form
 * writes it), so this reads as staff-controlled configuration, which is what
 * makes it safe to hide a surface on: a tenant cannot lock itself out of a
 * page it could otherwise have switched on itself.
 */
export const useKmdEasyEnabled = (): boolean => {
  const { data } = useQuery<KmdEasyEnabledGate>(GET_KMD_EASY_ENABLED_GATE, {
    fetchPolicy: 'cache-first',
  })
  return data?.kmdEasyCompanySettings?.enabled === true
}

export default useKmdEasyEnabled
